import React, { Component } from "react";

import { B2CTrackingScripts, B2UTrackingScripts } from "./services";
import { useScript } from "@website-builder/utilities/utils/useScript.js";
import { InitialiseHeap } from "./services/b2c/Heap";
import { InitialiseVero } from "./services/b2c/Vero";
import { InitialiseGTM } from "./services/shared/GoogleTagManager";
import { AddReferrer } from "@website-builder/utilities/utils/AddReferrer.js";
import { getCookie } from "@website-builder/utilities/utils/utils.js";
import { LiveChatLoaderProvider, Intercom } from "react-live-chat-loader";

global.loadSbAnalytics = true;
const BUSINESS_VERTICAL = process.env.GATSBY_BUSINESS_VERTICAL;

/** Load on first paint; defer heavy pixels to first user interaction for Lighthouse Best Practices. */
const IMMEDIATE_SCRIPTS = new Set(["Segment", "NewRelic", "GoogleTagManager"]);

const DEFERRED_FALLBACK_MS = 15000;

const clearDeferredScheduling = () => {
	if (typeof window === "undefined") return;
	if (window.__sbDeferredFallbackId) {
		clearTimeout(window.__sbDeferredFallbackId);
		window.__sbDeferredFallbackId = null;
	}
	if (window.__sbDeferredBoundHandler && window.__sbDeferredListenerOpts) {
		window.__sbDeferredListenerOpts.forEach(({ type, options }) => {
			window.removeEventListener(
				type,
				window.__sbDeferredBoundHandler,
				options,
			);
		});
		window.__sbDeferredListenerOpts = null;
		window.__sbDeferredBoundHandler = null;
	}
	window.__sbDeferredListenersActive = false;
};

const runDeferredAnalyticsPhase = () => {
	if (global.analyticsDeferredComplete) {
		return;
	}
	global.analyticsDeferredComplete = true;
	global.loadSbAnalytics = false;
	clearDeferredScheduling();

	const payload = window.__sbDeferredAnalyticsPayload;
	if (!payload) {
		return;
	}

	const {
		scriptsConfiguration,
		TrackingScripts,
		serviceConfigs,
		otherScripts,
		context,
		serviceConfigsProps,
		isRebrandPage,
	} = payload;

	Object.keys(scriptsConfiguration || {}).forEach((key) => {
		if (IMMEDIATE_SCRIPTS.has(key)) {
			return;
		}
		if (scriptsConfiguration[key] && TrackingScripts[key]) {
			TrackingScripts[key](
				serviceConfigsProps,
				context?.assetPrefixPath,
				context?.scriptPrefixPath,
				isRebrandPage,
			);
		}
	});

	if (otherScripts && otherScripts.length) {
		otherScripts.forEach(({ src, async, body, script_location }) => {
			useScript(src, async, body, script_location);
		});
	}

	if (BUSINESS_VERTICAL === "b2c" || BUSINESS_VERTICAL === "rb-b2c") {
		if (scriptsConfiguration && serviceConfigs) {
			if (scriptsConfiguration.Heap && TrackingScripts.Heap && serviceConfigs) {
				InitialiseHeap(serviceConfigs);
			}
			if (scriptsConfiguration.Vero && TrackingScripts.Vero && serviceConfigs) {
				InitialiseVero(serviceConfigs);
			}
		}
	}

	// Vero loads in deferred phase; fire pageview after init
	const gtmInternalUser = getCookie("gtm_internal_user");
	if (
		gtmInternalUser !== "internal" &&
		BUSINESS_VERTICAL !== "b2u" &&
		window._veroq
	) {
		window._veroq.push(["trackPageview"]);
	}
};

const ensureDeferredAnalyticsScheduled = (payload) => {
	if (typeof window === "undefined") {
		return;
	}
	if (global.analyticsDeferredComplete) {
		return;
	}
	window.__sbDeferredAnalyticsPayload = payload;

	if (window.__sbDeferredListenersActive) {
		// SPA navigated before first interaction — keep listeners but use latest layout scripts.
		return;
	}
	window.__sbDeferredListenersActive = true;

	const boundHandler = () => {
		runDeferredAnalyticsPhase();
	};

	const listenerTypes = [
		"scroll",
		"mousemove",
		"touchstart",
		"keydown",
		"click",
	];
	const options = { passive: true };
	window.__sbDeferredBoundHandler = boundHandler;
	window.__sbDeferredListenerOpts = listenerTypes.map((type) => ({
		type,
		options,
	}));
	listenerTypes.forEach((type) => {
		window.addEventListener(type, boundHandler, options);
	});

	window.__sbDeferredFallbackId = window.setTimeout(() => {
		runDeferredAnalyticsPhase();
	}, DEFERRED_FALLBACK_MS);
};

class Analytics extends Component {
	constructor(props) {
		super(props);
		this.state = {
			data: "",
			load: false,
		};
		this.renderIntercom =
			this.props?.pageAnalytics?.["Intercom"] ||
			this.props?.trackingScripts?.["Intercom"] ||
			false;
	}

	async componentDidMount() {
		if (global.analyticsDeferredComplete) {
			this.pageEvents();
			return;
		}

		if (!global.loadSbAnalytics) {
			this.pageEvents();
			return;
		}

		let scriptsConfiguration =
			this.props?.pageAnalytics || this.props?.trackingScripts;
		scriptsConfiguration = scriptsConfiguration || {};
		console.info("Selected scriptsConfiguration ==> ", scriptsConfiguration);

		let TrackingScripts;
		AddReferrer();
		if (BUSINESS_VERTICAL === "b2c" || BUSINESS_VERTICAL === "rb-b2c") {
			TrackingScripts = B2CTrackingScripts;
		} else if (BUSINESS_VERTICAL === "b2u") {
			TrackingScripts = B2UTrackingScripts;
		}

		if (global.analyticsPhase1Done) {
			ensureDeferredAnalyticsScheduled({
				scriptsConfiguration,
				TrackingScripts,
				serviceConfigs: window.__sbServiceConfigsCache,
				otherScripts: this.props.otherScripts,
				context: this.props.context,
				serviceConfigsProps: this.props.serviceConfigs,
				isRebrandPage: this.props.context?.isRebrandPage,
			});
			this.pageEvents();
			return;
		}

		global.analyticsPhase1Done = true;

		["Segment", "NewRelic", "GoogleTagManager"].forEach((key) => {
			if (scriptsConfiguration[key] && TrackingScripts[key]) {
				TrackingScripts[key](
					this.props.serviceConfigs,
					this.props.context?.assetPrefixPath,
					this.props.context?.scriptPrefixPath,
					this.props.context?.isRebrandPage,
				);
			}
		});

		let serviceConfigs;
		if (BUSINESS_VERTICAL == "b2c" || BUSINESS_VERTICAL == "rb-b2c") {
			try {
				const baseUrl =
					this.props.baseUrl ||
					`${window.location.protocol}//${window.location.host}`;

				await fetch(`${baseUrl}/curriculum/api/service-config/`, {
					credentials: "include",
				})
					.then((res) => res.json())
					.then((res) => (serviceConfigs = res));

				this.setState({ data: serviceConfigs });
				window.__sbServiceConfigsCache = serviceConfigs;

				if (scriptsConfiguration) {
					if (
						scriptsConfiguration["GoogleTagManager"] &&
						TrackingScripts["GoogleTagManager"] &&
						serviceConfigs
					) {
						console.log("LOADING GOOGLE_TAG_MANAGER ... !");
						InitialiseGTM(
							null,
							true,
							serviceConfigs,
							this.props?.context?.scriptPrefixPath,
						);
					}
				}
			} catch (err) {
				console.error("ERROR IN FETCHING /service-config", { err });
			}
		} else if (BUSINESS_VERTICAL == "b2u") {
			if (
				scriptsConfiguration["GoogleTagManager"] &&
				TrackingScripts["GoogleTagManager"]
			) {
				const workshopId = this.props.workshopId;
				console.log("workshopId", workshopId);
				InitialiseGTM(workshopId, false);
			}
		}

		ensureDeferredAnalyticsScheduled({
			scriptsConfiguration,
			TrackingScripts,
			serviceConfigs,
			otherScripts: this.props.otherScripts,
			context: this.props.context,
			serviceConfigsProps: this.props.serviceConfigs,
			isRebrandPage: this.props.context?.isRebrandPage,
		});

		this.pageEvents();
	}

	pageEvents = () => {
		const gtmInternalUser = getCookie("gtm_internal_user");
		if (gtmInternalUser !== "internal" && BUSINESS_VERTICAL !== "b2u") {
			if (window.analytics) {
				window.analytics.page();
			}

			if (window._veroq) {
				window._veroq.push(["trackPageview"]);
			}

			if (BUSINESS_VERTICAL == "b2c" || BUSINESS_VERTICAL == "rb-b2c") {
				const _hsq = (window._hsq = window._hsq || []);
				_hsq.push(["setPath", window.location.pathname]);
				_hsq.push(["trackPageView"]);
			}

			window.dataLayer = window.dataLayer || [];
		}
	};
	render() {
		return this.renderIntercom ? (
			<div className="intercom-facade">
				<LiveChatLoaderProvider
					providerKey="in1abue5"
					provider="intercom"
					idlePeriod={0}
				>
					<Intercom />
				</LiveChatLoaderProvider>
			</div>
		) : null;
	}
}

export default Analytics;
export { default as analyticsTransform } from "./transform";
